// APIs
import { sendOTP, verifyOTP } from '@tob/shared/otp/api';

// Constants & enums
import { config } from '@shopee-user/platform';
import { Env } from '@shopee-user/global';
import { VcodeOperation } from '@shopee-user/otp-constants';
import {
  AuthKeyType,
  OtpType,
  SendOtpResult,
} from '@tob/shared/otp/base-hook/constants';
import {
  OTP_COUNTDOWN_TIME_IN_TEST_ENVIRONMENT,
  OTP_COUNTDOWN_TIME,
} from '@tob/shared/otp/base-hook/constants';
import { ErrorCode } from '@tob/shared/api-constants';
import {
  Feature as StaticFeature,
  isFeatureEnabled as isStaticFeatureEnabled,
} from '@shopee-user/static-feature-toggle';

// Utils
import { getAuthenticationKeyValues } from './utils';

// Types
import type { SendOtpApi, VerifyOtpApi } from '@tob/shared/otp/base-hook/types';
import { VerifyOtpApiData } from './types';
import { OtpSessionInfo } from '@tob/shared/otp/api/types';

const defaultCooldown =
  config.ENV === Env.Test
    ? OTP_COUNTDOWN_TIME_IN_TEST_ENVIRONMENT
    : OTP_COUNTDOWN_TIME;

const isOtpSessionSupported = isStaticFeatureEnabled(
  StaticFeature.OtpSessionSupport,
  false
);

const getRemainingResendCountdown = (sessionInfo?: OtpSessionInfo) => {
  return isOtpSessionSupported &&
    sessionInfo?.remaining_resend_cooldown_sec !== null &&
    sessionInfo?.remaining_resend_cooldown_sec !== undefined
    ? sessionInfo.remaining_resend_cooldown_sec
    : defaultCooldown;
};

export const apiSendOtpWrapper: ({
  setWhatsappChannelId,
}: {
  setWhatsappChannelId: (whatsappChannel: string) => void;
}) => SendOtpApi<VcodeOperation, undefined> = ({ setWhatsappChannelId }) => {
  return async ({
    authKey = '',
    authKeyType = AuthKeyType.Phone,
    captchaSignature,
    operation,
    channel,
    token,
    supportedChannels,
  }) => {
    const defaultChannel = OtpType.WhatsappAuthLink;
    const { phone, token: avToken } = getAuthenticationKeyValues({
      authKey,
      authKeyType,
      token,
    });
    const otpChannel = channel || defaultChannel;
    const { data, error, error_msg } = await sendOTP({
      phone,
      avToken,
      captchaSignature,
      channel: otpChannel,
      operation,
      supportedChannels,
    });
    setWhatsappChannelId(data?.whatsapp_channel || '');

    let result: SendOtpResult;
    switch (error) {
      case ErrorCode.NoError:
        result = SendOtpResult.Success;
        break;
      case ErrorCode.NeedCaptcha:
      case ErrorCode.InvalidCaptcha:
        result = SendOtpResult.NeedCaptcha;
        break;
      default:
        result = SendOtpResult.Error;
        break;
    }

    return {
      result,
      latestSentChannel: otpChannel,
      availableChannels: data?.available_channel_list || [],
      hiddenChannels: data?.hidden_available_channel_list || [],
      remainingResendCountdown: getRemainingResendCountdown(data?.session_info),
      trackingId: '',
      error,
      error_msg: error === ErrorCode.FraudOTP ? data?.prompt_msg : error_msg,
    };
  };
};

export const apiVerifyOtp: VerifyOtpApi<
  VcodeOperation,
  VerifyOtpApiData
> = async ({
  otp,
  operation,
  phone,
  email,
  encryptedEmail,
  mToken,
  avToken,
}: {
  otp: string;
  operation: VcodeOperation;
  phone?: string;
  email?: string;
  encryptedEmail?: string;
  mToken?: string;
  avToken?: string;
}) => {
  const { request, response } = await verifyOTP({
    otp,
    operation,
    phone,
    email,
    encryptedEmail,
    mToken,
    avToken,
  });
  const { data, error, error_msg } = response;
  return {
    otpToken: data?.otp_token || '',
    data: {
      request,
    },
    error,
    error_msg,
  };
};
